import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { highlightHtmlText, findTargetChunkIndex, resetScrollState, removeExistingHighlights } from './highlight-html'
import { parseHtmlChunks } from './parseHtmlChunks'
import LazyIframe from './VirtualizedHtml'
import HtmlOptionsBar from './HtmlOptionsBar'

const ViewHtml = ({
  selectedCitation,
  htmlContent,
  currentLang,
  iframeRef,
  document,
  setDocument,
  showTranslationMenu
}) => {
  const [isLoadingDocument, setIsLoadingDocument] = useState(true)
  const lazyIframeRef = useRef(null)
  const highlightTimeoutRef = useRef(null)
  const lastHighlightedIdRef = useRef(null)
  const isUserScrollingRef = useRef(false)
  const userScrollTimeoutRef = useRef(null)
  // Track whether we triggered the scroll (so we can ignore our own scroll events)
  const isProgrammaticScrollRef = useRef(false)

  // Parse HTML into chunks (memoized)
  const { styles, dir: docDir, chunks } = useMemo(() => {
    if (!htmlContent) return { styles: '', dir: '', chunks: [] }
    return parseHtmlChunks(htmlContent)
  }, [htmlContent])

  // Loading state when content changes
  useEffect(() => {
    setIsLoadingDocument(true)
    lastHighlightedIdRef.current = null
    resetScrollState()

    const timeout = setTimeout(() => {
      setIsLoadingDocument(false)
    }, 500)

    return () => {
      clearTimeout(timeout)
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current)
    }
  }, [htmlContent])

  // Called when LazyIframe has written the shell and first batch
  const handleIframeReady = useCallback((iframeDocument) => {
    if (!iframeDocument) return

    const handleLinkClick = (event) => {
      const target = event.target.closest('a')
      if (!target) return

      const href = target.getAttribute('href')
      if (!href) return

      event.preventDefault()
      event.stopPropagation()

      if (href.startsWith('#')) {
        const targetId = href.substring(1)
        const targetElement = iframeDocument.getElementById(targetId)
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        return
      }
    }

    iframeDocument.addEventListener('click', handleLinkClick, true)

    // Track user scrolling inside iframe (but ignore our own programmatic scrolls)
    const iframeWindow = iframeDocument.defaultView
    if (iframeWindow) {
      iframeWindow.addEventListener('scroll', () => {
        // Skip if this scroll was triggered by us
        if (isProgrammaticScrollRef.current) return

        isUserScrollingRef.current = true
        if (userScrollTimeoutRef.current) {
          clearTimeout(userScrollTimeoutRef.current)
        }
        userScrollTimeoutRef.current = setTimeout(() => {
          isUserScrollingRef.current = false
        }, 800)
      })
    }

    if (iframeRef) {
      iframeRef.current = lazyIframeRef.current?.getIframeRef() || null
    }
  }, [iframeRef])

  // Single effect for scroll + highlight — no debounce, no re-render cascade
  useEffect(() => {
    console.log('[ViewHtml] scroll+highlight effect triggered', {
      isLoadingDocument,
      hasCitation: !!selectedCitation,
      citationId: selectedCitation?.id,
      section_title: selectedCitation?.metadata?.section_title,
      chunksLength: chunks.length,
      lastHighlightedId: lastHighlightedIdRef.current,
      hasLazyIframe: !!lazyIframeRef.current,
    })

    if (isLoadingDocument || !selectedCitation || chunks.length === 0) {
      console.log('[ViewHtml] EARLY RETURN — guard failed', {
        isLoadingDocument,
        noCitation: !selectedCitation,
        noChunks: chunks.length === 0,
      })
      return
    }

    const citationId = selectedCitation.id
    if (lastHighlightedIdRef.current === citationId) {
      console.log('[ViewHtml] EARLY RETURN — same citationId, skipping', { citationId })
      return
    }

    // Clear any pending highlight from previous citation
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current)
      highlightTimeoutRef.current = null
    }

    // Immediately clear old highlights
    const iframeDoc = lazyIframeRef.current?.getIframeDocument()
    if (iframeDoc) removeExistingHighlights(iframeDoc)

    const textToHighlight = selectedCitation.page_content ||
      selectedCitation.metadata?.page_content ||
      selectedCitation.text

    if (!textToHighlight) {
      console.log('[ViewHtml] EARLY RETURN — no text to highlight')
      return
    }

    // Find target chunk
    const targetChunkIndex = findTargetChunkIndex(chunks, textToHighlight, selectedCitation)
    console.log('[ViewHtml] citation click', {
      citationId,
      targetChunkIndex,
      section: selectedCitation?.metadata?.section_title,
      textLength: textToHighlight?.length,
      totalChunks: chunks.length,
    })

    if (targetChunkIndex >= 0 && lazyIframeRef.current) {
      // Mark scroll as programmatic so our scroll listener ignores it
      isProgrammaticScrollRef.current = true
      isUserScrollingRef.current = false
      if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current)

      // Load chunks up to target and scroll
      lazyIframeRef.current.scrollToChunk(targetChunkIndex)

      // Reset programmatic scroll flag after animation settles
      setTimeout(() => { isProgrammaticScrollRef.current = false }, 1000)
    }

    // Highlight after a short delay for DOM to settle (chunk injection is synchronous,
    // but scrollIntoView animation needs a moment)
    highlightTimeoutRef.current = setTimeout(() => {
      const doc = lazyIframeRef.current?.getIframeDocument()
      if (!doc) return

      const highlightedElement = highlightHtmlText(
        textToHighlight,
        doc,
        selectedCitation?.metadata?.language || 'fr',
        'yellow',
        selectedCitation,
        { skipScroll: true }
      )

      if (highlightedElement) {
        lastHighlightedIdRef.current = citationId
        // Always scroll the highlighted element into view — scrollToChunk only scrolls
        // to the chunk container, which doesn't help when multiple articles share one chunk
        highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        console.log('[ViewHtml] highlighted + scrolled to element', { citationId })
      } else {
        console.warn('[ViewHtml] highlight failed', { citationId, section: selectedCitation?.metadata?.section_title })
      }
    }, 150)

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
        highlightTimeoutRef.current = null
      }
    }
  }, [selectedCitation, isLoadingDocument, chunks])

  // Clear highlighting when no citation is selected
  useEffect(() => {
    if (!selectedCitation) {
      lastHighlightedIdRef.current = null
      resetScrollState()
      const iframeDoc = lazyIframeRef.current?.getIframeDocument()
      if (iframeDoc) removeExistingHighlights(iframeDoc)
    }
  }, [selectedCitation])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetScrollState()
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current)
      if (userScrollTimeoutRef.current) clearTimeout(userScrollTimeoutRef.current)
    }
  }, [])

  return (
    <div
      className='relative'
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <HtmlOptionsBar
        documentDoc={document}
        selectedCitation={selectedCitation}
        iframeRef={iframeRef}
        setDocument={setDocument}
        showTranslationMenu={showTranslationMenu}
      />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: isLoadingDocument ? 'none' : 'block'
        }}
      >
        {chunks.length > 0 && (
          <LazyIframe
            ref={lazyIframeRef}
            chunks={chunks}
            styles={styles}
            dir={docDir || (currentLang === 'ar' ? 'rtl' : 'ltr')}
            onIframeReady={handleIframeReady}
          />
        )}
      </div>

      {/* Loading state */}
      {isLoadingDocument && (
        <div
          className="flex items-center justify-center"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f8f9fa'
          }}
        >
          <div className="text-gray-500">Loading document...</div>
        </div>
      )}
    </div>
  )
}

export default ViewHtml
